/**
 * Identity + auth-flow primitives for the Command Center kernel.
 *
 * Identity (UserSchema) is email-only per slice 3 / issue #109 — no
 * display name, no role, no avatar. The auth-flow schemas below carry
 * the wire contract for sign-in, strategy commit, and phone
 * registration; they are the boundary between the worker's auth routes
 * and the SPA's sign-in state machine. ADR-0093 owns the strategy
 * model.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export const UserSchema = z.object({
  email: z.string().email(),
});

export type User = z.infer<typeof UserSchema>;

// ---------------------------------------------------------------------------
// Auth strategy + wire contract (ADR-0093)
// ---------------------------------------------------------------------------

/**
 * Per-operator sticky strategy stored in D1 `operator_auth.strategy`.
 *
 * `email-only` is the unbound default — present when a row exists but
 * no factor has been committed, and indistinguishable on the wire from
 * the no-row case. It is not a usable third subsequent-sign-in factor
 * today (a future ADR would promote it with a cutoff date).
 */
export const AuthStrategySchema = z.enum(["email-only", "sms", "passkey"]);
export type AuthStrategy = z.infer<typeof AuthStrategySchema>;

/**
 * Body of POST /api/auth/sms/verify (subsequent SMS sign-in). The
 * operator's strategy is already committed to `sms` and their phone
 * is already verified; this verifies the code Twilio just delivered.
 */
export const SmsVerifyRequestSchema = z.object({
  email: z.string().email(),
  code: z.string().regex(/^\d{6}$/),
});
export type SmsVerifyRequest = z.infer<typeof SmsVerifyRequestSchema>;
